export const PUBLIC_ALPHA_MAX_AGE_HOURS = 168;

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asDate(value) {
  const date = new Date(value || '');
  return Number.isFinite(date.getTime()) ? date : null;
}

export function alphaSnapshotId(dashboard = {}) {
  if (dashboard.snapshot_id) return dashboard.snapshot_id;
  const generatedAt = String(dashboard.generated_at || '').trim();
  if (!generatedAt) return null;
  const compactTimestamp = generatedAt.replace(/[^0-9]/g, '').slice(0, 14);
  const closedCount = Number(dashboard.paper_performance?.closed_count || 0);
  const verifiedEvents = Number(dashboard.trade_audit?.verified_event_count || 0);
  return `alpha-${compactTimestamp || 'unknown'}-${Math.max(0, closedCount)}c-${Math.max(0, verifiedEvents)}e`;
}

export function alphaLedgerSync(dashboard = {}) {
  const marks = asArray(dashboard.paper_performance?.trade_marks);
  const closedMarks = marks.filter((mark) => String(mark?.state || '').toUpperCase() === 'CLOSED');
  const expectedClosedCount = Number(dashboard.paper_performance?.closed_count || 0);
  const countsMatch = expectedClosedCount === closedMarks.length;
  const auditStatus = String(dashboard.trade_audit?.status || '').toUpperCase();
  return {
    status: countsMatch && auditStatus === 'OK' ? 'SYNCED' : 'MISMATCH',
    snapshot_id: alphaSnapshotId(dashboard),
    expected_closed_count: expectedClosedCount,
    published_closed_count: closedMarks.length,
    counts_match: countsMatch,
    audit_status: auditStatus || 'UNKNOWN'
  };
}

export function validatePublicAlphaDashboard(payload, {
  now = new Date(),
  maxAgeHours = PUBLIC_ALPHA_MAX_AGE_HOURS
} = {}) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Public Alpha snapshot is not a JSON object.');
  }
  if (payload.public_snapshot !== true) {
    throw new Error('Public Alpha snapshot is not marked public-safe.');
  }
  for (const key of ['decision_tickets', 'source_alerts', 'positions', 'outcomes']) {
    if (!Array.isArray(payload[key]) || payload[key].length > 0) {
      throw new Error(`Public Alpha snapshot contains private ${key} rows.`);
    }
  }

  const generatedAt = asDate(payload.generated_at);
  const nowDate = asDate(now) || new Date();
  if (!generatedAt) throw new Error('Public Alpha snapshot has no valid generated_at timestamp.');
  const ageHours = (nowDate.getTime() - generatedAt.getTime()) / 3_600_000;
  if (ageHours < -0.25) throw new Error('Public Alpha snapshot timestamp is unexpectedly in the future.');
  if (Number.isFinite(Number(maxAgeHours)) && ageHours > Number(maxAgeHours)) {
    throw new Error(`Public Alpha snapshot is stale (${ageHours.toFixed(1)}h old).`);
  }

  const sync = alphaLedgerSync(payload);
  if (!payload.ledger_sync) {
    throw new Error('Public Alpha snapshot has no ledger synchronization metadata.');
  }
  if (!sync.counts_match) {
    throw new Error(`Public Alpha closed ledger is incomplete (${sync.published_closed_count}/${sync.expected_closed_count}).`);
  }
  if (String(payload.ledger_sync.status || '').toUpperCase() !== 'SYNCED') {
    throw new Error(`Public Alpha ledger reports ${payload.ledger_sync.status}.`);
  }
  return payload;
}
